import { z } from 'zod'

/**
 * A time signature.
 *
 * ⚠️ The two field names are INVERTED with respect to what they hold — read them through
 * `meterTop()` / `meterBottom()` from `@tunasong/models`, never positionally:
 *
 * - `notesPerBar` is the **TOP** number — the number of beats in a bar (the 7 of 7/8).
 * - `beatsPerBar` is the **BOTTOM** number — the note value that gets one beat (the 8 of 7/8).
 *
 * The names are wrong for historical reasons and are kept deliberately: they are the persisted
 * DynamoDB attribute names, this schema is embedded in the song entity, and renaming them makes
 * `isSong()` return false for every already-stored song that has a meter. There is no
 * entity-migration mechanism, so the rule is fix-at-the-use-site, never migrate persisted user
 * data.
 *
 * The inversion is not theoretical: it cost us a transposed `[beatsPerBar, notesPerBar]` pair
 * handed to Tone's `Transport.timeSignature`, which made every bar position, loop point, `"1m"`
 * duration and metronome bar wrong in any non-4/4 song — a bar of 3/4 ran 5.333 beats and 7/8 ran
 * 4.571. 4/4 is symmetric under the transposition, which is why it survived so long.
 */
export const meterSchema = z.object({
  notesPerBar: z.union([
    z.literal(2),
    z.literal(3),
    z.literal(4),
    z.literal(5),
    z.literal(6),
    z.literal(7),
    z.literal(8),
    z.literal(9),
    z.literal(10),
    z.literal(11),
  ]),
  beatsPerBar: z.union([z.literal(4), z.literal(8)]),
})

export type Meter = z.infer<typeof meterSchema>

export const rhythmSchema = z.object({
  tempo: z.number().optional(),
  meter: meterSchema.optional(),
  triplet: z.boolean().optional(),
})

export type Rhythm = z.infer<typeof rhythmSchema>
